/**
 * Single place for indigent form rules that vary by tenant (subdomain).
 *
 * How to extend:
 * - Defaults below encode current “global” behaviour (demo/victorkhanye cell optional, etc.).
 * - Add a `Partial<IndigentTenantRules>` patch for a tenant, or introduce a small
 *   `TENANT_PATCHES` map if many tenants need overrides.
 *
 * Keep feature flags that are not form-validation (e.g. Verification button visibility)
 * in `featureUtils.ts`; this file is only for indigent wizard field requirements.
 */
import { getSubdomain } from '@/utils/tenantUtils';

export type IndigentTenantRules = {
  /** Applicant cell number required on personal info step */
  cellNumberRequired: boolean;
  /** Show email field after cell number */
  emailVisible: boolean;
  /** When email is shown, it is required */
  emailRequired: boolean;
  /** Electricity meter number required on address step */
  electricityMeterRequired: boolean;
  /** Municipal account number required on address step */
  accountNumberRequired: boolean;
  /** Proof of address upload required on supporting documents step */
  proofOfAddressRequired: boolean;
  /** Certified ID upload required (supporting documents step) */
  certifiedIdRequired: boolean;
  /**
   * When set, Erf No must be numeric only and length ≤ this (digits).
   * `null` = no extra constraint (any non-empty string).
   */
  erfNoMaxDigits: number | null;
};

const CELL_OPTIONAL_SUBDOMAINS = new Set(['demo', 'victorkhanye']);

/** Tenants where municipal account numbers are not always available. */
const ACCOUNT_OPTIONAL_SUBDOMAINS = new Set(['ephraim', 'demo']);

/** Tenants that do not require certified ID upload (legacy / policy). */
const CERTIFIED_ID_OPTIONAL_SUBDOMAINS = new Set(['ephraim']);

const LANGEBERG_PATCH: Partial<IndigentTenantRules> = {
  cellNumberRequired: true,
  emailVisible: true,
  emailRequired: false,
  electricityMeterRequired: false,
  proofOfAddressRequired: true,
  erfNoMaxDigits: 4,
};

function baseRules(subdomainLower: string): IndigentTenantRules {
  return {
    cellNumberRequired: !CELL_OPTIONAL_SUBDOMAINS.has(subdomainLower),
    emailVisible: false,
    emailRequired: false,
    electricityMeterRequired: false,
    accountNumberRequired: !ACCOUNT_OPTIONAL_SUBDOMAINS.has(subdomainLower),
    proofOfAddressRequired: false,
    certifiedIdRequired: !CERTIFIED_ID_OPTIONAL_SUBDOMAINS.has(subdomainLower),
    erfNoMaxDigits: null,
  };
}

/**
 * Resolved rules for the current tenant (subdomain).
 * Call from components/hooks; values are stable for a given page load.
 */
export function getIndigentTenantRules(): IndigentTenantRules {
  const subdomain = getSubdomain()?.toLowerCase();
  if (!subdomain) {
    // No subdomain (e.g. bare localhost): match strict defaults — cell required, certified ID required
    return {
      cellNumberRequired: true,
      emailVisible: false,
      emailRequired: false,
      electricityMeterRequired: false,
      accountNumberRequired: true,
      proofOfAddressRequired: false,
      certifiedIdRequired: true,
      erfNoMaxDigits: null,
    };
  }

  const base = baseRules(subdomain);
  if (subdomain === 'langeberg') {
    return { ...base, ...LANGEBERG_PATCH };
  }
  return base;
}
